import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { useLocation } from "react-router-dom/cjs/react-router-dom.min";
import axios from "axios";

const ContactDialog = ({ open, handleClose, estate: estateProp }) => {
  const { state } = useLocation();
  const estate = estateProp || state?.property;
  const PDF_FILE_URL = estate?.projectBrochureUrl;

  const [status, setStatus] = useState({
    loading: false,
    success: false,
    error: "",
    popupBlocked: false,
    spreadsheetError: "",
  });

  const apiIp = process.env.REACT_APP_API_PUBLIC_AP

  const handleSubmit = async (formData) => {
    const { name, email, phone } = formData;
    let brochureUrl;
    let brochureWindow;

    try {
      if (!PDF_FILE_URL) throw new Error("Brochure URL is missing.");
      brochureUrl = new URL(PDF_FILE_URL, window.location.href);
      if (!["http:", "https:"].includes(brochureUrl.protocol)) {
        throw new Error("Unsupported brochure URL protocol.");
      }
      brochureWindow = window.open("about:blank", "_blank");
      if (brochureWindow) brochureWindow.opener = null;
    } catch {
      setStatus({
        loading: false,
        success: false,
        error: "This brochure link is invalid. Please contact us for assistance.",
        popupBlocked: false,
      });
      return;
    }

    setStatus({
      loading: true,
      success: false,
      error: "",
      popupBlocked: false,
      spreadsheetError: "",
    });
    try {
      await axios.post(
        `${apiIp}/api/email/send-email`,
        {
          email,
          name,
          phoneNo: phone,
          description: "From Brochure",
          source: `Property brochure request${estate?.projectTitle ? `: ${estate.projectTitle}` : ""}`,
        }
      );

      let spreadsheetError = "";
      try {
        await axios.post(
          `${apiIp}/api/spreedsheet/sheet-data-update`,
          {
            email,
            name,
            phoneNo: phone,
            description: "From Brochure",
            source: `Property brochure request${estate?.projectTitle ? `: ${estate.projectTitle}` : ""}`,
          }
        );
      } catch (err) {
        spreadsheetError =
          err.response?.data?.message ||
          "Your request was emailed, but we couldn't record it in our spreadsheet.";
      }
      setStatus({
        loading: false,
        success: true,
        error: "",
        popupBlocked: !brochureWindow,
        spreadsheetError,
      });
      if (brochureWindow) {
        brochureWindow.location.href = brochureUrl.href;
        if (!spreadsheetError) handleClose();
      }
    } catch (err) {
      if (brochureWindow) brochureWindow.close();
      setStatus({
        loading: false,
        success: false,
        error: err.response?.data?.message || "We couldn't send your details. Please try again.",
        popupBlocked: false,
        spreadsheetError: "",
      });
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        component: "form",
        onSubmit: (event) => {
          event.preventDefault();
          const formData = new FormData(event.currentTarget);
          const formJson = Object.fromEntries(formData.entries());
          handleSubmit(formJson);
        },
        sx: {
          borderRadius: 1,
          backgroundColor: "#f6f4ef",
          boxShadow: "0 28px 90px rgba(20, 19, 16, 0.24)",
        },
      }}
    >
      <DialogTitle
        sx={{
          px: { xs: 3, sm: 5 },
          pt: 4,
          color: "#292720",
          fontFamily: 'Georgia, "Times New Roman", serif',
          fontSize: "1.8rem",
        }}
      >
        Request the brochure
      </DialogTitle>
      <DialogContent sx={{ px: { xs: 3, sm: 5 }, pb: 1 }}>
        <Typography sx={{ mb: 2, color: "text.secondary", fontSize: "0.88rem" }}>
          Share your contact details to receive more information about {estate?.projectTitle}.
        </Typography>
        {status.success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Your details were emailed successfully.
            {status.popupBlocked && (
              <a
                href={PDF_FILE_URL}
                target="_blank"
                rel="noopener noreferrer"
                style={{ display: "block", marginTop: 8, color: "#292720" }}
              >
                Open brochure
              </a>
            )}
          </Alert>
        )}
        {status.spreadsheetError && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {status.spreadsheetError}
          </Alert>
        )}
        {status.error && <Alert severity="error" sx={{ mb: 2 }}>{status.error}</Alert>}
        <TextField
          margin="normal"
          id="name"
          name="name"
          required
          fullWidth
          label="Name"
          variant="outlined"
          sx={brochureFieldStyles}
        />
        <TextField
          margin="normal"
          id="email"
          name="email"
          required
          fullWidth
          label="Email"
          type="email"
          variant="outlined"
          sx={brochureFieldStyles}
        />
        <TextField
          margin="normal"
          id="phone"
          name="phone"
          required
          fullWidth
          label="Phone Number"
          type="tel"
          variant="outlined"
          sx={brochureFieldStyles}
        />
      </DialogContent>
      <DialogActions sx={{ px: { xs: 3, sm: 5 }, pb: 4, pt: 2 }}>
        <LoadingButton
          loading={status.loading}
          onClick={handleClose}
          color="inherit"
        >
          {" "}
          Cancel
        </LoadingButton>
        <LoadingButton
          loading={status.loading}
          type="submit"
          variant="contained"
          sx={{
            px: 3,
            py: 1.2,
            borderRadius: 0,
            backgroundColor: "#292720",
            fontSize: "0.68rem",
            fontWeight: 600,
            letterSpacing: "0.1em",
          }}
        >
          {" "}
          Submit
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default ContactDialog;

const brochureFieldStyles = {
  "& .MuiOutlinedInput-root": {
    borderRadius: 0,
    backgroundColor: "rgba(255,255,255,0.72)",
    "& fieldset": { borderColor: "#d9d5ca" },
    "&:hover fieldset": { borderColor: "#a68757" },
    "&.Mui-focused fieldset": { borderColor: "#a68757", borderWidth: 1 },
  },
};